import PropTypes from 'prop-types';
import React, { useEffect, useState, useContext } from 'react';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import { useHistory } from 'react-router-dom';
import CountdownTimer from '../../componentsV3/CountdownTimer';
import BasicModal from './BasicModal';
import { fetcher } from '../../../utility/domUtils';
import { ACTION, FPTI_SPEC } from '../../utilsV3/fptiConstants';
import _get from 'lodash/get';
import { isLoggedInUser } from '../../utilsV3/authUtils';
import {
  extendSessionAPI,
  addActivityListeners,
  removeActivityListeners,
} from '../extendSession/extendSessionUtils';
import { useSelector } from 'react-redux';
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react';
import { MessageFormat } from '@paypalcorp/worldready';
const { sysColorAccent2Main } = PAYPAL_THEME;

const ExtendSession = (props) => {
  const worldReady = useContext(WorldReadyContext);
  const continueButton = new MessageFormat(worldReady, {
    id: 'buttonsV3.extendSession.continueButton',
  }).format();
  const dialogHeader = new MessageFormat(worldReady, {
    id: 'buttonsV3.extendSession.dialogHeader',
  }).format();
  const dialogMessage = new MessageFormat(worldReady, {
    id: 'buttonsV3.extendSession.dialogMessage',
  }).format();

  const { heartbeatInterval = 1000 } = props;

  const application = useSelector((state) => state.application);
  const isLoggedIn = isLoggedInUser(application);
  const isSCACountry = _get(application, 'isSCACountry');

  // Session time should last 5 or 15 minutes according to the region
  const countryTimeout = isSCACountry ? 5 : 15;

  const confirmTime = (countryTimeout - 2) * 60 * 1000;
  const expireTime = countryTimeout * 60 * 1000;
  const activityFrame = 1 * 60 * 1000;

  const history = useHistory();

  const [isOpen, setIsOpen] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isActivityListenersAdded, setIsActivityListenerersAdded] =
    useState(false);
  const [lastActivityAt, setLastActivityAt] = useState(Date.now());

  const logoutUserAndForceLogin = () => {
    const current_location = window.location.href;
    const returnUri = current_location.substr(
      current_location.indexOf('/buttons')
    );
    const renderURL = '/donate/buttons/login?returnUri=' + returnUri;
    fetcher('/donate/sessionlogout').finally(() => {
      history.push(renderURL);
    });
    location.reload();
  };

  const requestExtendSession = async () => {
    if (isSending) return;
    setIsSending(true);
    await extendSessionAPI({ currentStep: props.currentStep })
      .then(() => {
        setIsOpen(false);
      })
      .catch(() => {
        logoutUserAndForceLogin();
      });
    setIsSending(false);
  };

  const setUserActivityDetected = () => {
    removeActivityListeners(setUserActivityDetected);
    requestExtendSession();
    setIsActivityListenerersAdded(false);
    setLastActivityAt(Date.now());
  };

  const fetchCallListener = () => {
    setLastActivityAt(Date.now());
  };

  const fptiSpec = {
    impression: FPTI_SPEC.ALERT_MODAL_IM,
    data: {
      action: ACTION.EXTEND_SESSION,
      step: props.currentStep,
    },
  };

  useEffect(() => {
    // If the user is not logged in we should never show the modal
    if (!isLoggedIn) return;

    const heartbeat = setInterval(() => {
      const currentTime = Date.now();
      if (currentTime - lastActivityAt > expireTime) {
        logoutUserAndForceLogin();
        return;
      }
      if (currentTime - lastActivityAt > confirmTime) {
        if (isActivityListenersAdded) {
          removeActivityListeners(setUserActivityDetected);
          setIsActivityListenerersAdded(false);
        }
        if (!isOpen) setIsOpen(true);
      } else if (currentTime - lastActivityAt > confirmTime - activityFrame) {
        if (!isActivityListenersAdded) {
          addActivityListeners(setUserActivityDetected);
          setIsActivityListenerersAdded(true);
        }
      }
    }, heartbeatInterval);

    document.addEventListener('fetchCall', fetchCallListener);

    // Clear window events if the component is unmounted
    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('fetchCall', fetchCallListener);
    };
  }, [lastActivityAt, isOpen]);

  useEffect(() => {
    if (isTimeout) {
      logoutUserAndForceLogin();
    }
  }, [isTimeout]);

  return (
    <BasicModal
      className="modal-overlay timeout-modal-header"
      id="extend-session-dialog"
      buttonLabel={continueButton}
      buttonState={isSending ? 'processing' : 'initial'}
      buttonOnClick={requestExtendSession}
      buttonTheme="v1"
      closeOnBackgroundClick={false}
      header={dialogHeader}
      headerCustomStyles={{ fontSize: '2rem' }}
      iconType="hourglass"
      isOpen={isOpen}
      message1={dialogMessage}
      messageOneCustomStyles={{ fontSize: '1rem' }}
      onClose={requestExtendSession}
      fptiSpec={fptiSpec}
    >
      <CountdownTimer
        expireTime={expireTime}
        lastActivity={lastActivityAt}
        pause={isSending}
        onTimeout={setIsTimeout}
        styles={{ color: sysColorAccent2Main, fontSize: '2rem' }}
      />
    </BasicModal>
  );
};

ExtendSession.propTypes = {
  heartbeatInterval: PropTypes.number,
  currentStep: PropTypes.number,
};

export default ExtendSession;
